import { nextTick, onMounted, ref, useTemplateRef, Ref, ShallowRef } from "vue";
import type { VForm } from "vuetify/components";

export type FormValidity = {
    form: Readonly<ShallowRef<VForm | null>>;
    valid: Ref<boolean | null>;
    onValidity: (value: boolean | null) => void;
    seedValidity: () => Promise<void>;
    validate: () => Promise<{ valid: boolean }> | undefined;
    reset: () => void;
    resetValidation: () => void;
};

/**
 * The v-form validity wiring shared by the form shells (FormCard, DialogCard).
 *
 * `valid` is seeded false so a freshly-opened form with unmet required fields
 * keeps its confirm button disabled. The seed validates silently on mount:
 * validate() then resetValidation() reads validity without leaving the
 * required fields painted as errors before the user has touched them.
 *
 * The caller's template must put `ref="form"` on its v-form.
 */
export function useFormValidity(): FormValidity {
    const valid = ref<boolean | null>(false);
    const form = useTemplateRef<VForm>("form");

    function onValidity(value: boolean | null): void {
        valid.value = value;
    }

    async function seedValidity(): Promise<void> {
        await nextTick();
        const instance = form.value;
        if (!instance) return;
        if (instance.items.length === 0) {
            valid.value = true;
            return;
        }
        const { valid: result } = await instance.validate();
        valid.value = result;
        instance.resetValidation();
    }

    function validate() {
        return form.value?.validate();
    }
    function reset() {
        return form.value?.reset();
    }
    function resetValidation() {
        return form.value?.resetValidation();
    }

    onMounted(seedValidity);

    return { form, valid, onValidity, seedValidity, validate, reset, resetValidation };
}
